import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useSchemaOrg, getLocalBusinessSchema, getFAQSchema } from '../hooks/useSchemaOrg';
import { MapPin, Users, ChevronDown, Clock, Shield, CreditCard, Phone, CheckCircle, Star, ArrowRight, Luggage, Plane, Award, Quote, Calendar, Mail, MessageSquare, ChevronUp, Send, MessageCircle, Route } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomCalendar from '../components/CustomCalendar';
import CustomTimePicker from '../components/CustomTimePicker';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Beautiful Messinia images
const images = {
  hero: 'https://images.unsplash.com/photo-1649002883100-adc1088884fa?auto=format&fit=crop&w=1920&q=80',
  voidokilia: 'https://images.unsplash.com/photo-1683576657137-ed29e701e1a3?auto=format&fit=crop&w=800&q=80',
  beach: 'https://images.pexels.com/photos/30413157/pexels-photo-30413157.jpeg?auto=compress&cs=tinysrgb&w=800',
  luxuryCar: 'https://images.unsplash.com/photo-1768363341895-74756d1fc99d?auto=format&fit=crop&w=800&q=80',
  oliveGrove: 'https://images.unsplash.com/photo-1695470393142-4c14422ffb9a?auto=format&fit=crop&w=800&q=80',
  coastalRoad: 'https://images.unsplash.com/photo-1765288116079-d3901a45d333?auto=format&fit=crop&w=800&q=80',
  kalamata: 'https://images.pexels.com/photos/32600256/pexels-photo-32600256.jpeg?auto=compress&cs=tinysrgb&w=800',
};

// Destination images for route cards
const destinationImages = {
  'kalamata-city-center': 'https://images.unsplash.com/photo-1555993539-1732b0258235?auto=format&fit=crop&w=600&q=80',
  'costa-navarino': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053739/Costa_Navarino_x8zsq1.jpg',
  'stoupa': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770054216/Stoupa_sycdw9.jpg',
  'kardamyli': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053745/Kardamyli_dmxome.jpg',
  'pilos': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053741/Pylos_s4zowe.jpg',
  'methoni': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053740/Methoni_x5uefq.jpg',
  'koroni': 'https://res.cloudinary.com/dq8nrqgjn/image/upload/v1770053739/Koroni_rwq6gw.jpg',
  'ancient-messene': 'https://images.unsplash.com/photo-1555993539-1732b0258235?auto=format&fit=crop&w=600&q=80',
  'gialova': images.kalamata,
  'foinikounta': images.beach,
  'default': images.coastalRoad,
};

// Testimonials data
const testimonials = [
  {
    id: 1,
    name: 'Sarah Johnson',
    location: 'London, UK',
    rating: 5,
    text: 'Excellent service! The driver was waiting for us at the airport with a name sign. Very professional and the car was spotless. Highly recommend for anyone visiting Messinia.',
    date: 'November 2024'
  },
  {
    id: 2,
    name: 'Michael Schmidt',
    location: 'Munich, Germany',
    rating: 5,
    text: 'We used Kalamata Airport Taxi for our family trip to Costa Navarino. The minivan was perfect for our group with all luggage. Great value for money!',
    date: 'October 2024'
  },
  {
    id: 3,
    name: 'Emma Rossi',
    location: 'Milan, Italy',
    rating: 5,
    text: 'Punctual, friendly, and very helpful. Our driver gave us great tips about the area. The booking process was simple and the price was exactly as quoted.',
    date: 'September 2024'
  },
];

// FAQ data
const faqs = [
  {
    question: 'How do I book a transfer?',
    answer: 'Simply use our booking form above, select your pickup and drop-off locations, choose your date and time, and complete your reservation. You\'ll receive instant confirmation via email.'
  },
  {
    question: 'What if my flight is delayed?',
    answer: 'We monitor all flights in real-time. If your flight is delayed, your driver will adjust their arrival time accordingly. We offer 60 minutes of free waiting time for airport pickups.'
  },
  {
    question: 'Can I cancel my booking?',
    answer: 'Yes, you can cancel your booking up to 24 hours before the scheduled pickup time for a full refund. Cancellations made within 24 hours may be subject to a cancellation fee.'
  },
  {
    question: 'Are child seats available?',
    answer: 'Yes, we provide child seats and booster seats free of charge. Please specify the number and ages of children when making your booking so we can prepare the appropriate seats.'
  },
  {
    question: 'How will I find my driver at the airport?',
    answer: 'Your driver will be waiting in the arrivals hall holding a sign with your name. You\'ll also receive the driver\'s phone number in your confirmation email for easy contact.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and cash payment to the driver. Online payment is recommended for a smoother experience.'
  },
  {
    question: 'How much does a taxi from Kalamata Airport cost?',
    answer: 'Prices vary by destination. A taxi from Kalamata Airport to Costa Navarino costs around €80, to Kalamata city center €25-35, to Stoupa €70-80, and to Kardamyli €65-75. All prices are fixed and agreed upfront with no hidden fees.'
  },
  {
    question: 'Is there Uber or Bolt at Kalamata Airport?',
    answer: 'Ride-sharing apps like Uber and Bolt have very limited coverage at Kalamata Airport and throughout Messinia. Pre-booking a private taxi transfer is the most reliable option to ensure you have transportation waiting when you arrive.'
  },
  {
    question: 'How far is Costa Navarino from Kalamata Airport?',
    answer: 'Costa Navarino is approximately 45km from Kalamata Airport, about 50 minutes by private taxi transfer. We provide direct, comfortable transfers to all Costa Navarino resorts including The Romanos, The Westin, and W Costa Navarino.'
  },
];

const NewHomePage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('10:00');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [popularRoutes, setPopularRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Sort locations: Kalamata Airport first, then alphabetically
  const sortLocations = (locs) => {
    return [...locs].sort((a, b) => {
      if (a.slug === 'kalamata-airport') return -1;
      if (b.slug === 'kalamata-airport') return 1;
      return a.name.localeCompare(b.name);
    });
  };

  // Filter locations based on search (name or region)
  const filteredFromLocations = sortLocations(locations.filter(loc => 
    loc.name.toLowerCase().includes(fromSearch.toLowerCase()) ||
    loc.region?.toLowerCase().includes(fromSearch.toLowerCase())
  ));
  const filteredToLocations = sortLocations(locations.filter(loc => 
    loc.name !== fromLocation && (
      loc.name.toLowerCase().includes(toSearch.toLowerCase()) ||
      loc.region?.toLowerCase().includes(toSearch.toLowerCase())
    )
  ));

  useEffect(() => {
    fetch(`${API_BASE}/locations`)
      .then(res => res.json())
      .then(data => setLocations(data))
      .catch(console.error);
    
    fetch(`${API_BASE}/routes/popular`)
      .then(res => res.json())
      .then(data => setPopularRoutes(data.slice(0, 6)))
      .catch(console.error);

    fetch(`${API_BASE}/vehicles/homepage`)
      .then(res => res.json())
      .then(data => setVehicles(data))
      .catch(console.error);

    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPickupDate(tomorrow);
    setPickupTime('10:00');

    // Close dropdowns on outside click
    const handleClick = () => {
      setShowFromDropdown(false);
      setShowToDropdown(false);
      setShowPassengerDropdown(false);
      setShowCalendar(false);
      setShowTimePicker(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const formatDateForUrl = (date) => {
    if (!date) return '';
    // Use local date components to avoid timezone shift issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTimeDisplay = (time) => {
    if (!time) return 'Select time';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const handleSearch = () => {
    if (fromLocation && toLocation) {
      const fromSlug = locations.find(l => l.name === fromLocation)?.slug;
      const toSlug = locations.find(l => l.name === toLocation)?.slug;
      if (fromSlug && toSlug) {
        navigate(`/routes/${fromSlug}/${toSlug}`);
      }
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setContactSuccess(false), 5000);
      } else {
        console.error('Contact form submission failed');
      }
    } catch (error) {
      console.error('Contact form error:', error);
    } finally {
      setContactSubmitting(false);
    }
  };

  const getDestinationImage = (slug) => {
    return destinationImages[slug] || destinationImages.default;
  };

  // Set document meta tags
  useDocumentMeta({
    title: 'Kalamata Airport Taxi | Reliable Airport Transfers in Messenia, Greece',
    description: 'Professional taxi and airport transfer services from Kalamata Airport to Costa Navarino, Stoupa, Kardamyli, Pylos and all Messenia destinations. Fixed prices, 24/7 service, free cancellation.',
    canonical: 'https://kalamataairporttaxi.com',
    ogTitle: 'Kalamata Airport Taxi | Reliable Airport Transfers in Messenia',
    ogDescription: 'Professional taxi and airport transfer services from Kalamata Airport. Fixed prices, comfortable vehicles, experienced local drivers. Book online today.',
    ogUrl: 'https://kalamataairporttaxi.com',
    twitterTitle: 'Kalamata Airport Taxi | Reliable Airport Transfers',
    twitterDescription: 'Professional taxi and airport transfer services from Kalamata Airport. Fixed prices, 24/7 service.'
  });

  // Schema.org structured data for SEO
  useSchemaOrg([
    getLocalBusinessSchema(),
    getFAQSchema(faqs)
  ]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <Header />

      {/* Hero Section with Beautiful Image */}
      <section className="relative min-h-[80vh] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat overflow-hidden"
          style={{ backgroundImage: `url(${images.hero})` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#2d232e]/90 via-[#2d232e]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2d232e]/50 via-transparent to-[#2d232e]/30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-5">
              <Award className="w-4 h-4 text-[#36827f]" />
              <span className="text-sm font-medium text-white/90">Professional Airport Transfers</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-[1.1]">
              Kalamata<br />
              <span className="text-[#36827f]">Airport Taxi</span><br />
              <span className="text-3xl md:text-4xl lg:text-4xl font-normal text-white/80">Reliable Transfers</span>
            </h1>
            
            <p className="text-lg text-white/70 mb-6 max-w-lg leading-relaxed">
              Your trusted partner for airport transfers in Kalamata. Professional drivers, comfortable vehicles, and fixed prices.
            </p>

            {/* Stats */}
            <div className="flex items-center gap-6 mb-6">
              <div>
                <p className="text-2xl font-bold text-white">90+</p>
                <p className="text-xs text-white/60">Destinations</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div>
                <p className="text-2xl font-bold text-white">24/7</p>
                <p className="text-xs text-white/60">Support</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div>
                <p className="text-2xl font-bold text-white">5★</p>
                <p className="text-xs text-white/60">Service</p>
              </div>
            </div>
          </div>

          {/* Simple Booking Form - Aligned Left */}
          <div className="w-full mt-2 relative z-[60]">
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-4 max-w-3xl">
              <div className="flex flex-col lg:flex-row items-stretch">
                {/* From */}
                <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                  <div 
                    className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full"
                    onClick={() => { setShowFromDropdown(true); setShowToDropdown(false); }}
                  >
                    <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">From</p>
                      <input
                        type="text"
                        placeholder="Select pickup location"
                        value={showFromDropdown ? fromSearch : fromLocation}
                        onChange={(e) => { setFromSearch(e.target.value); setShowFromDropdown(true); }}
                        onFocus={() => { setShowFromDropdown(true); setFromSearch(''); }}
                        className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${fromLocation && !showFromDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                      />
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showFromDropdown ? 'rotate-180' : ''}`} />
                  </div>
                  {showFromDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[280px] overflow-y-auto">
                      {filteredFromLocations.length > 0 ? filteredFromLocations.map(loc => (
                        <div 
                          key={loc.slug}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                          onClick={() => { setFromLocation(loc.name); setFromSearch(''); setShowFromDropdown(false); }}
                        >
                          <p className="font-medium text-gray-900">{loc.name}</p>
                          <p className="text-sm text-gray-500">{loc.region}</p>
                        </div>
                      )) : (
                        <div className="px-4 py-3 text-gray-500 text-sm">No locations found</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:block w-px bg-gray-200 my-3" />

                {/* To */}
                <div className="relative flex-[1.5] min-w-0" onClick={(e) => e.stopPropagation()}>
                  <div 
                    className="flex items-center gap-3 px-5 py-5 hover:bg-gray-50 transition rounded-xl h-full"
                    onClick={() => { setShowToDropdown(true); setShowFromDropdown(false); }}
                  >
                    <MapPin className="w-6 h-6 text-[#36827f] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">To</p>
                      <input
                        type="text"
                        placeholder="Select destination"
                        value={showToDropdown ? toSearch : toLocation}
                        onChange={(e) => { setToSearch(e.target.value); setShowToDropdown(true); }}
                        onFocus={() => { setShowToDropdown(true); setToSearch(''); }}
                        className={`bg-transparent outline-none w-full truncate placeholder-gray-400 ${toLocation && !showToDropdown ? 'font-medium text-gray-900' : 'text-gray-900'}`}
                      />
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${showToDropdown ? 'rotate-180' : ''}`} />
                  </div>
                  {showToDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[280px] overflow-y-auto">
                      {filteredToLocations.length > 0 ? filteredToLocations.map(loc => (
                        <div 
                          key={loc.slug}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition"
                          onClick={() => { setToLocation(loc.name); setToSearch(''); setShowToDropdown(false); }}
                        >
                          <p className="font-medium text-gray-900">{loc.name}</p>
                          <p className="text-sm text-gray-500">{loc.region}</p>
                        </div>
                      )) : (
                        <div className="px-4 py-3 text-gray-500 text-sm">No locations found</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Search Button */}
                <button 
                  onClick={handleSearch}
                  disabled={!fromLocation || !toLocation}
                  className="m-3 px-10 py-5 rounded-xl font-bold text-white text-lg transition-all bg-[#36827f] hover:bg-[#2d6f6c] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#36827f]/20 hover:shadow-xl"
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="bg-[#2d232e] py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { icon: Clock, text: 'Meet & Greet Included' },
              { icon: Shield, text: 'Licensed Drivers' },
              { icon: CreditCard, text: 'Fixed Prices' },
              { icon: CheckCircle, text: 'Free Cancellation' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#36827f]/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#36827f]" />
                </div>
                <span className="text-white/80 font-medium text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
              POPULAR DESTINATIONS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-4">
              Explore Messinia
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              From pristine beaches to ancient ruins, discover the beauty of the Peloponnese
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popularRoutes.map(route => (
              <a
                key={route.route_id}
                href={`/routes/${route.from_slug}/${route.to_slug}`}
                className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={getDestinationImage(route.to_slug)}
                    alt={route.to_name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#2d232e] via-[#2d232e]/20 to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
                    <Plane className="w-4 h-4" />
                    <span>From {route.from_name}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#36827f] transition-colors">
                    {route.to_name}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-white/60 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {route.distance}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {route.duration}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white/60">from</p>
                      <p className="text-2xl font-bold text-[#36827f]">€{route.min_price || route.base_price}</p>
                    </div>
                  </div>
                </div>

                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                  <ArrowRight className="w-5 h-5 text-white" />
                </div>
              </a>
            ))}
          </div>

          {/* Keyword-rich text + CTAs */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              All Kalamata airport taxi prices are fixed and include meet & greet service, flight monitoring, and free cancellation up to 24 hours before pickup.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/routes"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all bg-[#2d232e] hover:bg-[#3d333e] shadow-lg hover:shadow-xl"
              >
                View All Routes
                <ArrowRight className="w-5 h-5" />
              </a>
              <a 
                href="#booking-form"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all bg-[#36827f] hover:bg-[#2d6b68] shadow-lg hover:shadow-xl"
              >
                Book Now
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Image Split */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Images Grid */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.voidokilia} alt="Voidokilia Beach Messinia" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.oliveGrove} alt="Messinia Olive Groves" className="w-full h-64 object-cover" />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.coastalRoad} alt="Peloponnese Coastal Road" className="w-full h-64 object-cover" />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img src={images.luxuryCar} alt="Kalamata Airport Taxi Service" className="w-full h-48 object-cover" />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#36827f] text-white px-6 py-4 rounded-2xl shadow-xl">
                <p className="text-3xl font-bold">1000+</p>
                <p className="text-sm text-white/80">Happy Customers</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
                WHY CHOOSE US
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-6">
                Why Choose Kalamata<br />Airport Taxi?
              </h2>
              <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                Book your Kalamata airport taxi with confidence. We offer fixed prices agreed upfront - no meters, no hidden fees, no surge pricing. Your gateway to experiencing the beauty of Messinia starts here.
              </p>

              <div className="space-y-6">
                {[
                  { icon: CheckCircle, title: 'Fixed Prices, No Surprises', desc: 'All-inclusive pricing with tolls, parking & meet and greet included.' },
                  { icon: Clock, title: 'Flight Monitoring & Free Waiting', desc: 'We track your flight. Delayed? Your driver adjusts. 60 minutes free waiting.' },
                  { icon: MapPin, title: 'Local Expertise', desc: 'Our drivers know every route in Messinia - from Costa Navarino to Kardamyli.' },
                  { icon: Shield, title: 'Meet & Greet at Arrivals', desc: 'Your driver waits with your name sign. No searching, no confusion.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#36827f]/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[#36827f]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas Section - Moved up for SEO priority */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
              SERVICE AREAS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-4">
              Kalamata Airport Taxi Destinations
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Your Gateway to Messinia & the Peloponnese
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg text-gray-600 mb-12 text-center">
              <p className="leading-relaxed">
                Our <strong>Kalamata airport taxi</strong> service covers all of <strong>Messinia</strong> – the most popular region of the <strong>Peloponnese</strong> for luxury tourism. We provide private transfers from Kalamata Airport to the world-famous <strong>Costa Navarino</strong> resorts, the picturesque villages of <strong>Kardamyli</strong> and <strong>Stoupa</strong> in the Mani peninsula, and historic destinations like <strong>Pylos</strong>, <strong>Methoni</strong>, and <strong>Ancient Messene</strong>.
              </p>
              <p className="leading-relaxed mt-4">
                Travelling further? We also serve destinations across the <strong>Peloponnese</strong>, including <strong>Ancient Olympia</strong>, <strong>Grecotel Riviera Olympia</strong>, <strong>Aldemar Resorts</strong>, <strong>Sparta</strong>, <strong>Monemvasia</strong>, and <strong>Nafplio</strong>. Every <strong>Kalamata taxi transfer</strong> includes meet and greet service and flight monitoring.
              </p>
            </div>

            {/* Destination Tags with Internal Links */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { name: 'Costa Navarino', slug: 'costa-navarino' },
                { name: 'Kardamyli', slug: 'kardamyli' },
                { name: 'Stoupa', slug: 'stoupa' },
                { name: 'Pilos', slug: 'pilos' },
                { name: 'Methoni', slug: 'methoni' },
                { name: 'Koroni', slug: 'koroni' },
                { name: 'Kalamata City', slug: 'kalamata-city-center' },
                { name: 'Foinikounta', slug: 'foinikounta' },
                { name: 'Gytheio', slug: 'gytheio' },
                { name: 'Kyllini Port', slug: 'kyllini-port' },
                { name: 'Olympia Riviera', slug: 'grecotel-olympia-riviera' },
                { name: 'Aldemar Resort', slug: 'aldemar-olympian-village' },
                { name: 'Monemvasia', slug: 'monemvasia' },
                { name: 'Nafplio', slug: 'nafplio' },
                { name: 'Athens', slug: 'athens-city-center' }
              ].map((destination, i) => (
                <a 
                  key={i}
                  href={`/routes/kalamata-airport/${destination.slug}`}
                  className="px-4 py-2 bg-white text-[#2d232e] rounded-full text-sm font-medium hover:bg-[#36827f] hover:text-white transition-colors shadow-sm"
                >
                  {destination.name}
                </a>
              ))}
            </div>

            <p className="text-center text-gray-500 text-sm mt-6">
              Click any destination to see prices and book your Kalamata airport transfer
            </p>
          </div>
        </div>
      </section>

      {/* Fleet Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
              OUR FLEET
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-4">
              Kalamata Airport Taxi Vehicles
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Choose from our selection of premium, well-maintained vehicles for your Kalamata airport transfer
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicles.map(vehicle => (
              <div 
                key={vehicle.vehicle_id}
                className="bg-gray-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all group relative"
              >
                {vehicle.is_popular && (
                  <div className="absolute top-3 right-3 bg-[#36827f] text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </div>
                )}
                {vehicle.is_best_price && !vehicle.is_popular && (
                  <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    BEST PRICE
                  </div>
                )}
                <div className="relative mb-4 rounded-xl p-4">
                  <img 
                    src={vehicle.image} 
                    alt={`${vehicle.type} - Kalamata Airport Taxi`}
                    className="w-full h-36 object-contain group-hover:scale-105 transition-transform"
                  />
                </div>
                <h3 className="font-bold text-[#2d232e] text-lg mb-1">{vehicle.type}</h3>
                <p className="text-sm text-gray-500 mb-3">{vehicle.subtitle}</p>
                <div className="flex items-center gap-4 text-sm mb-3">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Users className="w-4 h-4 text-[#36827f]" />
                    {vehicle.passengers} passengers
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Luggage className="w-4 h-4 text-[#36827f]" />
                    {vehicle.luggage} bags
                  </span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <span className="text-gray-500 text-sm">From</span>
                  <span className="text-2xl font-bold text-[#36827f]">€{vehicle.base_price}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-10 max-w-2xl mx-auto">
            All our Kalamata airport taxi vehicles are air-conditioned, non-smoking, and equipped with child seats upon request. Professional English-speaking drivers ensure a comfortable journey.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
              SIMPLE BOOKING
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-4">
              Book Your Kalamata Airport Taxi in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Quick, easy, and secure - your transfer is confirmed in minutes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '1',
                title: 'Select Your Route',
                description: 'Choose your pickup location (Kalamata Airport or hotel) and your destination. See instant pricing for all vehicle options.',
                icon: Route
              },
              {
                step: '2',
                title: 'Choose Date & Vehicle',
                description: 'Pick your travel date, arrival time, and select the vehicle that suits your group size and comfort preferences.',
                icon: Calendar
              },
              {
                step: '3',
                title: 'Pay Online or Cash',
                description: 'Complete your booking with secure online payment, or choose to pay cash directly to your driver upon arrival.',
                icon: CreditCard
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                {/* Connector Line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#36827f] to-[#36827f]/30"></div>
                )}
                
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow text-center relative">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#36827f] text-white font-bold flex items-center justify-center text-sm">
                    {item.step}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-[#36827f]/10 flex items-center justify-center mx-auto mb-6 mt-2">
                    <item.icon className="w-8 h-8 text-[#36827f]" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#2d232e] mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <a 
              href="#booking-form"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all bg-[#36827f] hover:bg-[#2d6b68] shadow-lg hover:shadow-xl"
            >
              Book Your Transfer Now
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-[#2d232e] via-[#3d333e] to-[#2d232e] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#36827f]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/20 text-[#36827f] text-sm font-semibold mb-4 border border-[#36827f]/30">
              TESTIMONIALS
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg">
              Don't just take our word for it – hear from our satisfied customers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.id}
                className={`rounded-2xl p-8 relative transform hover:-translate-y-2 transition-all duration-300 ${
                  index === 1 
                    ? 'bg-gradient-to-br from-[#36827f] to-[#2d6f6c] shadow-xl shadow-[#36827f]/25' 
                    : 'bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15'
                }`}
              >
                <Quote className={`w-12 h-12 absolute top-6 right-6 ${index === 1 ? 'text-white/20' : 'text-[#36827f]/30'}`} />
                
                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                {/* Text */}
                <p className={`mb-6 leading-relaxed ${index === 1 ? 'text-white/90' : 'text-white/70'}`}>
                  "{testimonial.text}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${
                    index === 1 
                      ? 'bg-white text-[#36827f]' 
                      : 'bg-[#36827f] text-white'
                  }`}>
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className={`font-bold ${index === 1 ? 'text-white' : 'text-white'}`}>{testimonial.name}</p>
                    <p className={`text-sm ${index === 1 ? 'text-white/70' : 'text-white/50'}`}>{testimonial.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
              FAQ
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-lg">
              Find answers to common questions about our service
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-[#2d232e] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#36827f] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* FAQ Closing Text */}
          <div className="text-center mt-12 max-w-2xl mx-auto">
            <p className="text-gray-600">
              Still have questions about our Kalamata airport taxi service? Contact us 24/7 and we'll be happy to help with your transfer booking.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
                CONTACT US
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2d232e] mb-6">
                Get in Touch
              </h2>
              <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                Have questions? We're here to help 24/7. Reach out via phone, email, or use the contact form.
              </p>

              <div className="space-y-6">
                <a href="tel:+306932421170" className="flex items-center gap-4 group">
                  <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center group-hover:bg-[#36827f] transition">
                    <Phone className="w-6 h-6 text-[#36827f] group-hover:text-white transition" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone (24/7)</p>
                    <p className="font-bold text-[#2d232e] text-lg">+30 693 242 1170</p>
                  </div>
                </a>

                <a href="https://wa.me/306932421170" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <div className="w-14 h-14 rounded-xl bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366] transition">
                    <MessageCircle className="w-6 h-6 text-[#25D366] group-hover:text-white transition" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">WhatsApp</p>
                    <p className="font-bold text-[#2d232e] text-lg">+30 693 242 1170</p>
                  </div>
                </a>

                <a href="mailto:info@kalamataairporttaxi.com" className="flex items-center gap-4 group">
                  <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center group-hover:bg-[#36827f] transition">
                    <Mail className="w-6 h-6 text-[#36827f] group-hover:text-white transition" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-bold text-[#2d232e] text-lg">info@kalamataairporttaxi.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#36827f]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-bold text-[#2d232e] text-lg">Kalamata Airport, Greece</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2d232e] mb-6">Send us a Message</h3>
              
              {contactSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
                  Thank you! Your message has been sent. We'll get back to you soon.
                </div>
              )}
              
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                  <input 
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 outline-none transition"
                    placeholder="John Smith"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input 
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 outline-none transition"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input 
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 outline-none transition"
                      placeholder="+30 xxx xxx xxxx"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 outline-none transition resize-none"
                    placeholder="How can we help you?"
                  />
                </div>
                <button 
                  type="submit"
                  disabled={contactSubmitting}
                  className="w-full px-6 py-4 rounded-xl font-bold text-white bg-[#36827f] hover:bg-[#2d6f6c] transition flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {contactSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewHomePage;
